import jsPDF from "jspdf";
import JSZip from "jszip";

export interface ConversionResult {
  success: boolean;
  message: string;
  filename?: string;
}

/**
 * Convert JPG image to PDF
 * @param file - Image file to convert
 * @returns ConversionResult with success status and message
 */
export const convertJPGToPDF = async (
  file: File
): Promise<ConversionResult> => {
  try {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      return {
        success: false,
        message: "Please select a valid image file",
      };
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return {
        success: false,
        message: "File size exceeds 10MB limit",
      };
    }

    // Read file as data URL
    const reader = new FileReader();

    return new Promise((resolve) => {
      reader.onload = async (event) => {
        try {
          const imgData = event.target?.result as string;

          // Create image element to get dimensions
          const img = new Image();
          img.onload = () => {
            // Create PDF with image dimensions
            const imgWidth = img.width;
            const imgHeight = img.height;

            // Calculate dimensions for PDF (in mm)
            // A4 dimensions: 210 x 297 mm
            const pdfWidth = 210;
            const pdfHeight = (imgHeight * pdfWidth) / imgWidth;

            const pdf = new jsPDF({
              orientation: pdfHeight > pdfWidth ? "portrait" : "landscape",
              unit: "mm",
              format: [pdfWidth, pdfHeight],
            });

            // Add image to PDF
            pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

            // Generate filename
            const originalName = file.name.replace(/\.[^/.]+$/, "");
            const filename = `${originalName}.pdf`;


            resolve({
              success: true,
              message: `Successfully converted ${file.name} to PDF`,
              filename,
            });
            // Note: callers can generate blob via pdf.output('blob') if needed
          };

          img.onerror = () => {
            resolve({
              success: false,
              message: "Failed to load image",
            });
          };

          img.src = imgData;
        } catch (error) {
          resolve({
            success: false,
            message: `Conversion error: ${error instanceof Error ? error.message : "Unknown error"}`,
          });
        }
      };

      reader.onerror = () => {
        resolve({
          success: false,
          message: "Failed to read file",
        });
      };

      reader.readAsDataURL(file);
    });
  } catch (error) {
    return {
      success: false,
      message: `Error: ${error instanceof Error ? error.message : "Unknown error"}`,
    };
  }
};

/**
 * Batch convert multiple JPG images to a single PDF
 * @param files - Array of image files to convert
 * @param outputFilename - Custom filename for the output PDF (default: "combined.pdf")
 * @returns ConversionResult with success status and message
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
/**
 * Generate a single PDF blob that contains all provided images as pages.
 * Returns a Blob (PDF) which the caller can save or further process.
 */
export const generateCombinedPDFBlob = async (
  files: File[],
  rotationMap: Map<string, number> = new Map(),
  quality: number = 0.92
): Promise<Blob> => {
  try {
    if (files.length === 0) {
      throw new Error("No files provided");
    }

    const pdf = new jsPDF();

    let first = true;

    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        throw new Error(`${file.name} is not a valid image file`);
      }

      const dataUrl = await readFileAsDataURL(file);
      const img = await loadImageElement(dataUrl);

      // apply rotation and draw to canvas to get normalized imageData
      const rotation = rotationMap.get(file.name) ?? 0;
      const imageData = drawImageToDataUrl(img, rotation, quality);

      const imgWidth = img.width;
      const imgHeight = img.height;
      const pdfWidth = 210; // mm
      const pdfHeight = (imgHeight * pdfWidth) / imgWidth;

      if (!first) pdf.addPage([pdfWidth, pdfHeight]);

      pdf.addImage(imageData, "JPEG", 0, 0, pdfWidth, pdfHeight);
      first = false;
    }

    const blob = pdf.output("blob");
    return blob;
  } catch (error) {
    throw error;
  }
};

/**
 * Generate separate PDF blobs (one per image) and return an array of { filename, blob }
 */
export const generateSeparatePDFBlobs = async (
  files: File[],
  rotationMap: Map<string, number> = new Map(),
  quality: number = 0.92
): Promise<{ filename: string; blob: Blob }[]> => {
  const result: { filename: string; blob: Blob }[] = [];

  for (const file of files) {
    if (!file.type.startsWith("image/")) continue;

    const dataUrl = await readFileAsDataURL(file);
    const img = await loadImageElement(dataUrl);
    const rotation = rotationMap.get(file.name) ?? 0;
    const imageData = drawImageToDataUrl(img, rotation, quality);

    const pdfWidth = 210;
    const pdfHeight = (img.height * pdfWidth) / img.width;
    const pdf = new jsPDF({ unit: "mm", format: [pdfWidth, pdfHeight] });
    pdf.addImage(imageData, "JPEG", 0, 0, pdfWidth, pdfHeight);

    const blob = pdf.output("blob");
    const originalName = file.name.replace(/\.[^/.]+$/, "");
    result.push({ filename: `${originalName}.pdf`, blob });
  }

  return result;
};

/* Helper utilities below */

const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

const loadImageElement = (dataUrl: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = dataUrl;
  });
};

/**
 * Draws image onto a canvas, applying rotation if needed, and returns JPEG data URL.
 */
const drawImageToDataUrl = (img: HTMLImageElement, rotation = 0, quality = 0.92): string => {
  const radians = (rotation % 360) * (Math.PI / 180);
  // if rotation is 90 or 270, swap dimensions
  const swap = Math.abs((rotation / 90) % 2) === 1;
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d")!;

  const w = swap ? img.height : img.width;
  const h = swap ? img.width : img.height;
  canvas.width = w;
  canvas.height = h;

  if (rotation === 0) {
    ctx.drawImage(img, 0, 0);
  } else {
    // translate to center, rotate, draw
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(radians);
    ctx.drawImage(img, -img.width / 2, -img.height / 2);
  }

  return canvas.toDataURL("image/jpeg", quality);
};

/**
 * Create a ZIP blob from an array of { filename, blob }
 */
export const createZipFromBlobs = async (files: { filename: string; blob: Blob }[]): Promise<Blob> => {
  const zip = new JSZip();
  for (const f of files) {
    zip.file(f.filename, f.blob);
  }
  const content = await zip.generateAsync({ type: "blob" });
  return content;
};

